import { useEffect, useState } from 'react';
import APIClient from '../api/api';
import { ContactFieldChange, ContactMigrationResult, ContactMigrationRun } from '../api/models';
import { Button, StatusTag } from './ui';
import {
  ChangeLabels,
  changeLabels,
  isTerminalRunStatus,
  privacyOutcomeLabel,
  privacyOutcomeTone,
  resultForDomain,
  resultStatusTone,
  runStatusTone,
} from './DomainContactMigration.model';

const DEFAULT_POLL_INTERVAL_MS = 2000;

export interface Props {
  /** Domain name (e.g. "example.com"). */
  domain: string;
  /** Override for tests. */
  pollIntervalMs?: number;
}

type RunState =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'polling'; workflowId: string; run: ContactMigrationRun | null }
  | { kind: 'done'; workflowId: string; run: ContactMigrationRun }
  | { kind: 'error'; message: string };

const DomainContactMigration = ({ domain, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS }: Props) => {
  const [state, setState] = useState<RunState>({ kind: 'idle' });

  const workflowId = state.kind === 'polling' ? state.workflowId : null;
  useEffect(() => {
    if (!workflowId) return;
    let cancelled = false;
    let timer: number | undefined;

    async function tick() {
      try {
        const run = await APIClient.getContactMigrationRun(workflowId!);
        if (cancelled) return;
        if (isTerminalRunStatus(run.status)) {
          setState({ kind: 'done', workflowId: workflowId!, run });
        } else {
          setState({ kind: 'polling', workflowId: workflowId!, run });
          timer = window.setTimeout(tick, pollIntervalMs);
        }
      } catch (e: any) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: e?.response?.data?.error || 'Failed to load the migration run.',
        });
      }
    }

    tick();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [workflowId, pollIntervalMs]);

  async function start() {
    setState({ kind: 'starting' });
    try {
      const { workflowId: id } = await APIClient.startContactMigration({ domain });
      setState({ kind: 'polling', workflowId: id, run: null });
    } catch (e: any) {
      setState({
        kind: 'error',
        message: e?.response?.data?.error || 'Failed to start the contact migration.',
      });
    }
  }

  const busy = state.kind === 'starting' || state.kind === 'polling';
  const shownRun =
    state.kind === 'done' ? state.run : state.kind === 'polling' ? state.run : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="text-[12.5px] text-text-2 max-w-[640px]">
        Runs the fast transfer contact migration for this domain only. Every gate still
        applies — account scope, feature flags, and configuration — and while the shadow
        flag is on, nothing is written: the run reports the changes a live write would
        make. A live run turns on domain privacy before it writes the new contacts.
      </div>
      <div>
        <Button variant="primary" size="sm" loading={busy} disabled={busy} onClick={start}>
          Run Contact Migration
        </Button>
      </div>

      {state.kind === 'error' && (
        <div className="text-[12.5px] text-danger" role="alert">
          {state.message}
        </div>
      )}

      {(state.kind === 'polling' || state.kind === 'done') && (
        <div className="flex flex-col gap-3 bg-panel border border-border rounded-md px-4 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">
              Workflow
            </span>
            <span className="font-mono text-[11.5px] text-text-2 break-all">
              {state.workflowId}
            </span>
            <StatusTag tone={runStatusTone(shownRun?.status ?? 'running')}>
              {shownRun?.status ?? 'running'}
            </StatusTag>
          </div>
          {state.kind === 'done' && (
            <RunResult run={state.run} domain={domain} />
          )}
        </div>
      )}
    </div>
  );
};

function RunResult({ run, domain }: { run: ContactMigrationRun; domain: string }) {
  const result = resultForDomain(run.summary, domain);
  if (!result) {
    return (
      <div className="text-[12.5px] text-text-3">
        The run finished without a result for this domain.
      </div>
    );
  }
  return <ResultDetails result={result} />;
}

function ResultDetails({ result }: { result: ContactMigrationResult }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">
          Result
        </span>
        <StatusTag tone={resultStatusTone(result.status)}>{result.status}</StatusTag>
        {result.privacyOutcome && (
          <StatusTag tone={privacyOutcomeTone(result.privacyOutcome)}>
            {privacyOutcomeLabel(result.privacyOutcome)}
          </StatusTag>
        )}
        {result.jobId != null && (
          <span className="font-mono text-[11.5px] text-text-3">job #{result.jobId}</span>
        )}
      </div>
      {result.privacyMessage && (
        <div className="text-[12.5px] text-text-2">Privacy: {result.privacyMessage}</div>
      )}
      {result.message && <div className="text-[12.5px] text-text-2">{result.message}</div>}
      {result.deferReason && (
        <div className="text-[12.5px] text-text-2">Deferred: {result.deferReason}</div>
      )}
      {result.fieldChanges && result.fieldChanges.length > 0 && (
        <FieldChangeTable changes={result.fieldChanges} labels={changeLabels(result.status)} />
      )}
    </div>
  );
}

function FieldChangeTable({
  changes,
  labels,
}: {
  changes: ContactFieldChange[];
  labels: ChangeLabels;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1.5">
        {labels.title} ({changes.length})
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="text-left text-[10px] uppercase tracking-wider text-text-3 font-semibold py-1 px-1">
                Field
              </th>
              <th className="text-left text-[10px] uppercase tracking-wider text-text-3 font-semibold py-1 px-1">
                {labels.old}
              </th>
              <th className="text-left text-[10px] uppercase tracking-wider text-text-3 font-semibold py-1 px-1">
                {labels.new}
              </th>
            </tr>
          </thead>
          <tbody>
            {changes.map((change) => (
              <tr key={change.field} className="border-t border-border align-top">
                <td className="py-1 px-1 font-mono text-[11.5px] text-text-2 whitespace-nowrap">
                  {change.field}
                </td>
                <td className="py-1 px-1 font-mono text-[11.5px] text-text-3 break-all">
                  <ContactValue value={change.old} />
                </td>
                <td className="py-1 px-1 font-mono text-[11.5px] text-text break-all">
                  <ContactValue value={change.new} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// An empty side is a real state — the migration fills in fields the registrar
// left blank — so it reads as empty rather than as a missing cell.
function ContactValue({ value }: { value: string }) {
  if (value === '') return <span className="text-text-3 italic">empty</span>;
  return <>{value}</>;
}

export default DomainContactMigration;
